import { Button, Card, Theme, Typography, makeStyles } from '@material-ui/core'
import Popper from '@material-ui/core/Popper'
import { MoreVert } from '@material-ui/icons'
import clsx from 'clsx'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { buttonHoverColor, dragIconColor } from 'bpm/shared/bpmColors'

const useStyles = makeStyles((theme: Theme) => ({
    popperWrapper: {
        zIndex: 1000,
        marginRight: theme.spacing(2),
    },
    actionsWrapper: {
        backgroundColor: theme.palette.common.white,
        padding: theme.spacing(1),
        borderRadius: theme.spacing(1),
    },
    copyMoveButton: {
        width: theme.spacing(12),
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: buttonHoverColor,
        },
    },
    bulkActionButton: {
        '&:hover': {
            cursor: 'pointer',
        },
        '&.disabled': {
            color: `${dragIconColor} !important`,
        },
    },
}))

type BulkActionButtonProps = {
    onBulkActionsClick?: (action: string) => void
    isBulkActionsEnabled?: boolean
}

const BulkActionsButton: FC<BulkActionButtonProps> = ({
    onBulkActionsClick = () => {},
    isBulkActionsEnabled = false,
}) => {
    const classes = useStyles()
    const [isMenuOpen, setIsMenuOpen] = useState(false)

    const buttonRef = useRef(null)
    const popperRef = useRef(null)

    const onBulkActionsButtonClick = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsMenuOpen(!isMenuOpen)
        },
        [isMenuOpen]
    )

    const handleBulkActionClick = useCallback(
        (action: string) => () => {
            onBulkActionsClick?.(action)
        },
        [onBulkActionsClick]
    )

    useEffect(() => {
        const handleClickOutside = (e: any) => {
            if (
                popperRef?.current &&
                !(popperRef.current as HTMLElement).contains(
                    e.target as HTMLElement
                ) &&
                !(
                    buttonRef?.current &&
                    (buttonRef?.current as HTMLElement).contains(e.target)
                )
            ) {
                setIsMenuOpen(false)
            }
        }

        window.addEventListener('click', handleClickOutside)

        return () => {
            window.removeEventListener('click', handleClickOutside)
        }
    }, [])

    return (
        <>
            <Button
                onClick={onBulkActionsButtonClick}
                data-testid="btnBulkActions"
                ref={buttonRef}
                disabled={!isBulkActionsEnabled}
                className={clsx(classes.bulkActionButton, {
                    disabled: !isBulkActionsEnabled,
                })}
            >
                Actions &nbsp; <MoreVert />
            </Button>

            <Popper
                open={isMenuOpen}
                placement={'bottom-end'}
                anchorEl={buttonRef?.current}
                disablePortal
                ref={popperRef}
                className={classes.popperWrapper}
            >
                <Card className={classes.actionsWrapper}>
                    <Typography
                        component="div"
                        className={classes.copyMoveButton}
                        onClick={handleBulkActionClick('copy')}
                    >
                        Copy
                    </Typography>
                    <Typography
                        component="div"
                        className={classes.copyMoveButton}
                        onClick={handleBulkActionClick('move')}
                    >
                        Move
                    </Typography>
                    <Typography
                        component="div"
                        className={classes.copyMoveButton}
                        onClick={handleBulkActionClick('delete')}
                    >
                        Delete
                    </Typography>
                </Card>
            </Popper>
        </>
    )
}

export default BulkActionsButton
